import { jwtDecode } from "jwt-decode";
import { getAuthToken } from "./cookie";

const getQueryParams = (params, url) => {
  let href = url;
  //this expression is to get the query strings
  let reg = new RegExp(`[?&]${params}=([^&#]*)`, "i");
  let queryString = reg.exec(href);
  return queryString ? queryString[1] : null;
};

const getUser = () => {
  const token = getAuthToken();
  return jwtDecode(token);
};

const range = (start, end) => {
  let length = end - start + 1;
  /*
  	Create an array of certain length and set the elements within it from
    start value to end value.
  */
  return Array.from({ length }, (_, idx) => idx + start);
};

const getDynoFieldsArguments = (node) => ({
  id: node.node.id,
  dynoTitle: node.node.dynoTitle,
  description: node.node.description,
  dynoPublishingStatus: node.node.dynoPublishingStatus,
  accessType: node.node.accessType,
  createdAt: node.node.createdAt,
  updatedAt: node.node.updatedAt,
  owner: node.node.owner,
  dynoType: node.node.dynoType,
  category: node.node.category,
});

const getUniqueDynoes = (dynoes, dynoesRes) => {
  const updatedDynoes = [...dynoes];
  if (dynoes.length === 0) return dynoesRes;
  for (const dynoe of dynoesRes) {
    let isDynoMatched = false;
    let dynoIndex = -1;
    for (const [index, dyno] of dynoes.entries()) {
      if (dyno.id === dynoe.id) {
        isDynoMatched = true;
        dynoIndex = index;
      }
    }
    if (isDynoMatched) {
      updatedDynoes[dynoIndex] = dynoe;
    } else {
      updatedDynoes.push(dynoe);
    }
  }
  return updatedDynoes;
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const validatePassword = (password) => {
  const re =
    // eslint-disable-next-line no-useless-escape
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/;
  return re.test(String(password));
};

const validatePhone = (phone) => {
  const re = /^\d{10}$/;
  return re.test(Number(phone));
};

const makeFirstLetterCapital = (word) => {
  return word
    .split("")
    .map((letter, index) => (index === 0 ? letter.toUpperCase() : letter))
    .join("");
};

const validateImage = (file, maxwidth, maxheight) => {
  let _URL = window.URL || window.webkitURL;
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.src = _URL.createObjectURL(file);
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (err) => {
      reject("Image size must be " + maxwidth + " X " + maxheight);
    };
  });
};

const validateVimeoURL = (url) => {
  return /^(https?:\/\/)?(www.)?(player.)?vimeo.com\/([a-z]*\/)*([0-9]{6,11})[?]?.*$/.test(
    url,
  );
};

const secondsToMinutesAndSeconds = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  return `${minutes}m:${seconds}sec`;
};

const getAvgTimeTakenPerQuestion = (totalSeconds, totalQuestions) => {
  if(totalQuestions === 0) {totalQuestions = 1}
  let averageTimePerQuestionInSeconds = Math.floor(
    totalSeconds / totalQuestions,
  );
  let remainingSeconds = Math.floor(totalSeconds % 60);
  let remainingAvgTimePerQuestionInSeconds = Math.round(
    remainingSeconds / totalQuestions,
  );
  return secondsToMinutesAndSeconds(
    averageTimePerQuestionInSeconds + remainingAvgTimePerQuestionInSeconds,
  );
};
const getDefaultValue = (submission, variable) => {
  const { id, defaultValue, variableType, variableAttributes: { } } = variable
  if (!submission) return defaultValue
  if (!submission.quizAnswers) return defaultValue
  const answer = submission.quizAnswers.find(x => x.variable.id === id)
  if (!answer) return defaultValue
  const vType = variableType.variableType
  const aType = typeof answer.value
  const varIsChecklist = vType === 'Checklist'
  const valueIsStr = aType === 'string'
  if (varIsChecklist && valueIsStr) {
    return answer.value
  }
  return answer.value
}

// Generated questions sometimes store math options wrapped in TeX delimiters ("$NH_4^+$"). The viewer
// renders math options with MathQuill, which wants bare LaTeX and shows the "$" signs literally.
const MATH_WRAPPED = /^\s*(?:\$\$?[\s\S]+\$\$?|\\\([\s\S]+\\\)|\\\[[\s\S]+\\\])\s*$/;

// Removes TeX delimiters from LaTeX. Escaped dollars (\$) are kept. No lookbehind, for older Safari.
const stripMathDelimiters = (text) =>
  String(text)
    .replace(/\\[()[\]]/g, "")
    .replace(/(^|[^\\])\$+/g, "$1")
    .trim();

const isImageChoice = (text) => /^https?:\/\/\S+\.(png|jpe?g|gif|svg|webp)(\?\S*)?$/i.test(String(text).trim());

// A choice field is treated as math when it is marked as equations, or when every text option is fully
// wrapped in delimiters (the generator sometimes wraps options but leaves equationOptions false).
const isMathChoiceField = (attrs) => {
  const choices = attrs?.choiceTexts;
  if (!Array.isArray(choices) || choices.length === 0) return false;
  if (attrs.equationOptions) return true;
  const textChoices = choices.filter((c) => typeof c === "string" && !isImageChoice(c));
  return textChoices.length > 0 && textChoices.every((c) => MATH_WRAPPED.test(c));
};

// Answer values must be cleaned the same way as the options: the viewer grades by comparing the chosen
// option's text with possibleAnswers. Checklist answers may be a JSON list.
const stripAnswerValue = (value, variableType) => {
  if (typeof value !== "string") return value;
  if (variableType === "Checklist") {
    try {
      const list = JSON.parse(value);
      if (Array.isArray(list)) {
        return JSON.stringify(list.map((v) => (typeof v === "string" ? stripMathDelimiters(v) : v)));
      }
    } catch (e) {
      // Not JSON (e.g. "7, 35"): clean it as plain text below.
    }
  }
  return stripMathDelimiters(value);
};

const normalizeMathVariable = (variable) => {
  const attrs = variable?.variableAttributes;
  if (!isMathChoiceField(attrs)) return variable;
  const variableType = variable.variableType?.variableType;
  return {
    ...variable,
    variableAttributes: {
      ...attrs,
      equationOptions: true,
      choiceTexts: attrs.choiceTexts.map((c) =>
        typeof c === "string" && !isImageChoice(c) ? stripMathDelimiters(c) : c,
      ),
    },
    defaultValue: stripAnswerValue(variable.defaultValue, variableType),
    possibleAnswers: Array.isArray(variable.possibleAnswers)
      ? variable.possibleAnswers.map((pa) => ({ ...pa, value: stripAnswerValue(pa.value, variableType) }))
      : variable.possibleAnswers,
  };
};

// Returns a copy of a dyno (as fetched) with math choice fields cleaned everywhere they appear: the
// fields, the submission variables used for grading, and saved answers shown on review. Apollo results
// are frozen, so nothing is mutated.
const normalizeDynoMath = (gqlDyno) => ({
  ...gqlDyno,
  variables: (gqlDyno.variables || []).map(normalizeMathVariable),
  submissionsVariables: (gqlDyno.submissionsVariables || []).map(normalizeMathVariable),
  ...(Array.isArray(gqlDyno.submissions)
    ? {
        submissions: gqlDyno.submissions.map((submission) => ({
          ...submission,
          quizAnswers: Array.isArray(submission?.quizAnswers)
            ? submission.quizAnswers.map((qa) => {
                const variable = normalizeMathVariable(qa.variable);
                return variable === qa.variable
                  ? qa
                  : { ...qa, variable, value: stripAnswerValue(qa.value, variable.variableType?.variableType) };
              })
            : submission?.quizAnswers,
        })),
      }
    : {}),
});

export {
  getQueryParams,
  getUser,
  range,
  getDynoFieldsArguments,
  getUniqueDynoes,
  validateEmail,
  validatePassword,
  validatePhone,
  makeFirstLetterCapital,
  validateImage,
  validateVimeoURL,
  getDefaultValue,
  normalizeDynoMath,
  getAvgTimeTakenPerQuestion,
  secondsToMinutesAndSeconds,
};
